/* Modulo: Collection Slider — scroll-snap rail controller.
   The rail swipes/scrolls natively (works with no JS); this wires the arrows,
   per-card dots, and optional autoplay (advances, wraps, pauses on hover/focus
   and via a persistent control). Vanilla IIFE, reduced-motion aware, theme-editor
   reload safe + leak-proof. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var REDUCE = !!(mq && mq.matches);
  if (mq && mq.addEventListener) mq.addEventListener('change', function (e) { REDUCE = e.matches; });

  function step(rail, card) {
    if (!card) return rail.clientWidth * 0.8;
    var cs = window.getComputedStyle(rail);
    var gap = parseFloat(cs.columnGap || cs.gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function init(root) {
    if (!root || root.dataset.moduloCsrReady === '1') return;
    root.dataset.moduloCsrReady = '1';

    var rail = root.querySelector('[data-csr-rail]');
    if (!rail) return;
    var cards = rail.querySelectorAll('.modulo-csr__card');
    if (cards.length < 2) return;

    var prev = root.querySelector('[data-csr-prev]');
    var next = root.querySelector('[data-csr-next]');
    var dotsHost = root.querySelector('[data-csr-dots]');
    var pp = root.querySelector('[data-csr-pp]');
    var rtl = window.getComputedStyle(rail).direction === 'rtl';

    var wantsAuto = root.dataset.autoplay === '1';
    var interval = (parseFloat(root.dataset.interval, 10) || 5) * 1000;
    var userPaused = false, hovering = false, timer = null;
    var dots = [];

    function maxScroll() { return rail.scrollWidth - rail.clientWidth; }
    function activeIndex() {
      var s = step(rail, cards[0]);
      if (!s) return 0;
      return Math.min(cards.length - 1, Math.max(0, Math.round(Math.abs(rail.scrollLeft) / s)));
    }
    function perView() {
      var s = step(rail, cards[0]);
      return s ? Math.max(1, Math.floor(rail.clientWidth / s)) : 1;
    }

    function buildDots() {
      if (!dotsHost) return;
      while (dotsHost.firstChild) dotsHost.removeChild(dotsHost.firstChild);
      dots = [];
      for (var i = 0; i < cards.length; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'modulo-csr__dot';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + cards.length);
        (function (idx) {
          b.addEventListener('click', function () {
            stop();
            rail.scrollTo({ left: idx * step(rail, cards[0]) * (rtl ? -1 : 1), behavior: REDUCE ? 'auto' : 'smooth' });
            start();
          });
        })(i);
        dotsHost.appendChild(b);
        dots.push(b);
      }
    }

    function syncDots() {
      var a = activeIndex();
      for (var i = 0; i < dots.length; i++) {
        if (i === a) dots[i].setAttribute('aria-current', 'true');
        else dots[i].removeAttribute('aria-current');
      }
    }
    function syncArrows() {
      var max = maxScroll(), pos = Math.abs(rail.scrollLeft);
      if (prev) prev.disabled = pos <= 2;
      if (next) next.disabled = pos >= max - 2;
    }
    function sync() { syncDots(); syncArrows(); }

    function page(dir) {
      var s = step(rail, cards[0]);
      var delta = dir * s * perView() * (rtl ? -1 : 1);
      rail.scrollBy({ left: delta, behavior: REDUCE ? 'auto' : 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { stop(); page(-1); start(); });
    if (next) next.addEventListener('click', function () { stop(); page(1); start(); });

    var raf = null;
    rail.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(sync);
    }, { passive: true });

    // keyboard: tab to a partially-off card scrolls it into view
    for (var c = 0; c < cards.length; c++) {
      cards[c].addEventListener('focus', (function (card) {
        return function () { card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: REDUCE ? 'auto' : 'smooth' }); };
      })(cards[c]));
    }

    /* ── autoplay ── */
    function canAuto() { return wantsAuto && !REDUCE && !userPaused && !hovering && root.isConnected; }
    function autoNext() {
      if (!root.isConnected || REDUCE) { stop(); return; }
      if (Math.abs(rail.scrollLeft) >= maxScroll() - 2) {
        rail.scrollTo({ left: 0, behavior: REDUCE ? 'auto' : 'smooth' });
      } else {
        page(1);
      }
    }
    function start() { if (!canAuto()) return; stop(); timer = setInterval(autoNext, interval); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    if (wantsAuto) {
      root.addEventListener('mouseenter', function () { hovering = true; stop(); });
      root.addEventListener('mouseleave', function () { hovering = false; start(); });
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
      if (pp) {
        pp.addEventListener('click', function () {
          userPaused = !userPaused;
          pp.classList.toggle('is-paused', userPaused);
          // The changing action-label carries the state; no aria-pressed, which
          // would announce an ambiguous "pressed" alongside a label that already
          // flips between "Play"/"Pause" (icon state is driven by .is-paused).
          pp.setAttribute('aria-label', userPaused ? 'Play slider' : 'Pause slider');
          if (userPaused) stop(); else start();
        });
      }
    }

    var rt = null;
    function onResize() { clearTimeout(rt); rt = setTimeout(sync, 100); }
    window.addEventListener('resize', onResize);

    root._csrStop = function () { stop(); window.removeEventListener('resize', onResize); };

    buildDots();
    sync();
    start();
  }

  function boot(scope) {
    var els = (scope || document).querySelectorAll('[data-modulo-csr]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-csr]') : null;
    if (el && typeof el._csrStop === 'function') el._csrStop();
  });
})();
